import React, { Suspense } from 'react'
import { Loader2 } from 'lucide-react'

// Loading fallback component
const LoadingFallback: React.FC<{ message?: string }> = ({ message = "Loading..." }) => (
  <div className="flex items-center justify-center min-h-[400px] w-full">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      <p className="text-sm text-gray-600 animate-pulse">{message}</p>
    </div>
  </div>
)

// Enhanced loading fallback with skeleton
const SkeletonLoader: React.FC = () => (
  <div className="space-y-6 p-6 animate-pulse">
    <div className="space-y-2">
      <div className="h-8 bg-gray-200 rounded w-1/4"></div>
      <div className="h-4 bg-gray-100 rounded w-1/2"></div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="bg-white p-6 rounded-lg border">
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-3 bg-gray-100 rounded w-1/2"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  </div>
)

// Lazy wrapper with error boundary
interface LazyWrapperProps {
  children: React.ReactNode
  fallback?: React.ComponentType
  skeleton?: boolean
  message?: string
}

export const LazyWrapper: React.FC<LazyWrapperProps> = ({ 
  children, 
  fallback: CustomFallback,
  skeleton = false,
  message
}) => {
  const FallbackComponent = CustomFallback || (skeleton ? SkeletonLoader : () => <LoadingFallback message={message} />)
  
  return (
    <Suspense fallback={<FallbackComponent />}>
      <div className="animate-in fade-in-0 duration-300">
        {children}
      </div>
    </Suspense>
  )
}

// Error boundary for lazy components
export class LazyErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ComponentType<{ error: Error }> },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ComponentType<{ error: Error }> }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback
      return <FallbackComponent error={this.state.error!} />
    }

    return this.props.children
  }
}

// Default error fallback
const DefaultErrorFallback: React.FC<{ error: Error }> = ({ error }) => (
  <div className="flex items-center justify-center min-h-[400px] w-full">
    <div className="text-center space-y-4 p-6">
      <div className="text-red-500">
        <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-900">Something went wrong</h3>
      <p className="text-sm text-gray-600 max-w-sm mx-auto">
        {error.message || "An unexpected error occurred while loading this section."}
      </p>
      <button 
        onClick={() => window.location.reload()} 
        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
      >
        Reload Page
      </button>
    </div>
  </div>
)

export default LazyWrapper